import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Sparkles, Lock, MessageSquare, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { useApp } from '@/components/AppLayout';
import { businessApi } from '@/lib/api';
import { useUpgradeModal } from '@/contexts/UpgradeModalContext';

interface ReminderSettings {
  basic_reminders_enabled: boolean;
  reminder_channel: 'email' | 'sms' | 'email_then_sms' | 'both';
  invoice_reminder_before_due_enabled: boolean;
  invoice_reminder_before_due_days: number;
  invoice_reminder_on_due_enabled: boolean;
  invoice_reminder_after_due_enabled: boolean;
  invoice_reminder_after_due_days: number;
  invoice_reminder_max_count: number;
  quote_reminder_after_send_enabled: boolean;
  quote_reminder_after_send_days: number;
  quote_reminder_before_expiry_enabled: boolean;
  quote_reminder_before_expiry_days: number;
  quote_reminder_on_expiry_enabled: boolean;
  intelligent_reminders_enabled: boolean;
}

export function AutomatedRemindersSettings() {
  const { nav } = useBusinessNav();
  const { currentBusiness, featureFlags, refreshUser } = useApp();
  const { showUpgradeModal } = useUpgradeModal();
  const [settings, setSettings] = useState<ReminderSettings | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // After loading check, featureFlags is guaranteed to be non-null
  const canUseSms = featureFlags?.can_use_sms_reminders ?? false;
  const canUseIntelligent = featureFlags?.can_use_intelligent_reminders ?? false;

  // Load settings from currentBusiness (only on mount or when business ID changes)
  useEffect(() => {
    if (currentBusiness) {
      setSettings({
        basic_reminders_enabled: currentBusiness.basic_reminders_enabled,
        reminder_channel: currentBusiness.reminder_channel,
        invoice_reminder_before_due_enabled: currentBusiness.invoice_reminder_before_due_enabled,
        invoice_reminder_before_due_days: currentBusiness.invoice_reminder_before_due_days,
        invoice_reminder_on_due_enabled: currentBusiness.invoice_reminder_on_due_enabled,
        invoice_reminder_after_due_enabled: currentBusiness.invoice_reminder_after_due_enabled,
        invoice_reminder_after_due_days: currentBusiness.invoice_reminder_after_due_days,
        invoice_reminder_max_count: currentBusiness.invoice_reminder_max_count,
        quote_reminder_after_send_enabled: currentBusiness.quote_reminder_after_send_enabled,
        quote_reminder_after_send_days: currentBusiness.quote_reminder_after_send_days,
        quote_reminder_before_expiry_enabled: currentBusiness.quote_reminder_before_expiry_enabled,
        quote_reminder_before_expiry_days: currentBusiness.quote_reminder_before_expiry_days,
        quote_reminder_on_expiry_enabled: currentBusiness.quote_reminder_on_expiry_enabled,
        intelligent_reminders_enabled: currentBusiness.intelligent_reminders_enabled,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBusiness?.id]); // Only reload when business ID changes, not on every refresh

  // Auto-save function (silent - no toast)
  const saveSettings = useCallback(async (settingsToSave: ReminderSettings) => {
    if (!currentBusiness) return;
    const response = await businessApi.update(currentBusiness.id, settingsToSave);
    if (response.data) {
      await refreshUser();
    }
  }, [currentBusiness, refreshUser]);

  const update = useCallback(<K extends keyof ReminderSettings>(key: K, value: ReminderSettings[K]) => {
    setSettings(prev => {
      if (!prev) return null;
      const newSettings = { ...prev, [key]: value };

      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Debounce save by 500ms
      saveTimeoutRef.current = setTimeout(() => {
        saveSettings(newSettings);
      }, 500);

      return newSettings;
    });
  }, [saveSettings]);

  // Wait for both settings and featureFlags to load to prevent flickering
  if (!settings || !featureFlags) {
    return (
      <div className="px-6 pt-6 pb-6 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="px-6 pt-6 pb-6">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          className="mb-3 -ml-2 text-muted-foreground"
          onClick={() => nav('/settings')}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Settings
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Automated Reminders</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Automatically follow up on unpaid invoices and pending quotes.
        </p>
      </div>

      {/* Intelligent Reminders Card */}
      <Card className={`mb-6 border-2 rounded-2xl ${
        settings.intelligent_reminders_enabled
          ? 'border-green-300 dark:border-green-700 bg-gradient-to-br from-green-50/50 to-emerald-50/50 dark:from-green-950/20 dark:to-emerald-950/20'
          : canUseIntelligent
            ? 'border-border'
            : 'border-purple-300 dark:border-purple-700 bg-gradient-to-br from-purple-50/50 to-blue-50/50 dark:from-purple-950/20 dark:to-blue-950/20'
      }`}>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              settings.intelligent_reminders_enabled
                ? 'bg-gradient-to-br from-green-500 to-emerald-500'
                : 'bg-gradient-to-br from-purple-500 to-blue-500'
            }`}>
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">Intelligent Reminders</CardTitle>
                {!canUseIntelligent && (
                  <span className="px-2 py-0.5 text-[10px] font-semibold bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full uppercase tracking-wide">
                    Premium
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Let AI optimize timing, channels, and messaging based on each client's behavior.
              </p>
            </div>
            {canUseIntelligent ? (
              <Switch
                checked={settings.intelligent_reminders_enabled}
                onCheckedChange={(checked) => update('intelligent_reminders_enabled', checked)}
              />
            ) : (
              <Lock className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4">
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <Clock className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${settings.intelligent_reminders_enabled ? 'text-green-500' : 'text-purple-500'}`} />
              Send at optimal times based on when each client reads emails
            </p>
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <MessageSquare className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${settings.intelligent_reminders_enabled ? 'text-green-500' : 'text-purple-500'}`} />
              Auto-switch to SMS for clients who ignore emails
            </p>
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <AlertCircle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${settings.intelligent_reminders_enabled ? 'text-green-500' : 'text-purple-500'}`} />
              Skip reminders for clients who always pay on time
            </p>
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <Sparkles className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${settings.intelligent_reminders_enabled ? 'text-green-500' : 'text-purple-500'}`} />
              AI-written messages personalized to each client
            </p>
          </div>
          {settings.intelligent_reminders_enabled ? (
            <div className="p-3 bg-white/70 dark:bg-black/20 rounded-lg border border-green-100 dark:border-green-900">
              <p className="text-xs text-muted-foreground">
                Intelligent mode is ON. The AI handles timing, channels, and messaging automatically. Basic settings below are disabled.
              </p>
            </div>
          ) : !canUseIntelligent ? (
            <>
              <div className="p-3 bg-white/70 dark:bg-black/20 rounded-lg border border-purple-100 dark:border-purple-900 mb-4">
                <p className="text-xs text-muted-foreground italic">
                  "John typically pays 2 days after receiving a reminder. We'll send his next reminder Tuesday at 9am — that's when he usually checks his email."
                </p>
              </div>
              <Button
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                onClick={() => nav('/settings/billing')}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Upgrade to Premium
              </Button>
            </>
          ) : null}
        </CardContent>
      </Card>

      {/* Basic Settings Card - content hidden when intelligent mode is on or basic reminders are off */}
      <Card className={`border-2 rounded-2xl ${
        settings.intelligent_reminders_enabled
          ? 'opacity-60'
          : settings.basic_reminders_enabled
            ? 'border-primary/30'
            : 'border-border'
      }`}>
        <CardHeader className={settings.intelligent_reminders_enabled || !settings.basic_reminders_enabled ? 'pb-4' : 'pb-2'}>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Basic Reminders</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Schedule reminders on a fixed timeline.
              </p>
            </div>
            {settings.intelligent_reminders_enabled ? (
              <span className="text-xs text-muted-foreground">Managed by Intelligent Mode</span>
            ) : (
              <Switch
                checked={settings.basic_reminders_enabled}
                onCheckedChange={(checked) => update('basic_reminders_enabled', checked)}
              />
            )}
          </div>
        </CardHeader>
        {/* Only show content when basic reminders is enabled AND intelligent mode is off */}
        {settings.basic_reminders_enabled && !settings.intelligent_reminders_enabled && (
        <CardContent className="space-y-5">
          {/* Channel */}
          <div className="flex items-center justify-between pb-3 border-b">
            <div>
              <div className="text-sm font-medium">Delivery channel</div>
              <div className="text-xs text-muted-foreground">How reminders are sent to clients</div>
            </div>
            <Select
              value={settings.reminder_channel}
              onValueChange={(v) => {
                const smsChannels = ['sms', 'email_then_sms', 'both'];
                if (smsChannels.includes(v) && !canUseSms) {
                  // Show upgrade modal for premium feature
                  showUpgradeModal({
                    code: 'limit_exceeded',
                    limit: 'sms_reminders',
                    message: 'SMS reminders require a premium subscription.',
                    current: 0,
                    max: 0,
                    upgrade_path: '/settings/billing',
                    current_plan: featureFlags?.plan || 'free',
                  });
                  return;
                }
                update('reminder_channel', v as ReminderSettings['reminder_channel']);
              }}
            >
              <SelectTrigger className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email only</SelectItem>
                <SelectItem value="sms" className={canUseSms ? '' : 'text-muted-foreground'}>
                  <span className="flex items-center gap-2">
                    <span className={canUseSms ? '' : 'opacity-50'}>SMS only</span>
                    {!canUseSms && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 rounded">
                        Premium
                      </span>
                    )}
                  </span>
                </SelectItem>
                <SelectItem value="email_then_sms" className={canUseSms ? '' : 'text-muted-foreground'}>
                  <span className="flex items-center gap-2">
                    <span className={canUseSms ? '' : 'opacity-50'}>Email, then SMS</span>
                    {!canUseSms && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 rounded">
                        Premium
                      </span>
                    )}
                  </span>
                </SelectItem>
                <SelectItem value="both" className={canUseSms ? '' : 'text-muted-foreground'}>
                  <span className="flex items-center gap-2">
                    <span className={canUseSms ? '' : 'opacity-50'}>Both email & SMS</span>
                    {!canUseSms && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 rounded">
                        Premium
                      </span>
                    )}
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Invoices */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Invoice Reminders</h3>
            <div className="space-y-1 rounded-lg border divide-y">
              {/* Before Due */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={settings.invoice_reminder_before_due_enabled}
                      onCheckedChange={(v) => update('invoice_reminder_before_due_enabled', !!v)}
                    />
                    <span className="text-sm font-medium">Before due date</span>
                  </label>
                  <div className="flex items-center gap-2 text-sm">
                    <Input
                      type="number"
                      min={1}
                      max={30}
                      value={settings.invoice_reminder_before_due_days}
                      onChange={(e) => update('invoice_reminder_before_due_days', Number(e.target.value) || 1)}
                      disabled={!settings.invoice_reminder_before_due_enabled}
                      className="w-16 h-9 text-center"
                    />
                    <span className="text-muted-foreground">days before</span>
                  </div>
                </div>
              </div>

              {/* On Due */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={settings.invoice_reminder_on_due_enabled}
                      onCheckedChange={(v) => update('invoice_reminder_on_due_enabled', !!v)}
                    />
                    <span className="text-sm font-medium">On due date</span>
                  </label>
                  <span className="text-sm text-muted-foreground">Morning of due date</span>
                </div>
              </div>

              {/* Overdue */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={settings.invoice_reminder_after_due_enabled}
                      onCheckedChange={(v) => update('invoice_reminder_after_due_enabled', !!v)}
                    />
                    <span className="text-sm font-medium">Overdue follow-ups</span>
                  </label>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Every</span>
                    <Input
                      type="number"
                      min={1}
                      max={30}
                      value={settings.invoice_reminder_after_due_days}
                      onChange={(e) => update('invoice_reminder_after_due_days', Number(e.target.value) || 1)}
                      disabled={!settings.invoice_reminder_after_due_enabled}
                      className="w-16 h-9 text-center"
                    />
                    <span className="text-muted-foreground">days, up to</span>
                    <Select
                      value={String(settings.invoice_reminder_max_count)}
                      onValueChange={(v) => update('invoice_reminder_max_count', Number(v))}
                      disabled={!settings.invoice_reminder_after_due_enabled}
                    >
                      <SelectTrigger className="w-28 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 time</SelectItem>
                        <SelectItem value="2">2 times</SelectItem>
                        <SelectItem value="3">3 times</SelectItem>
                        <SelectItem value="5">5 times</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quotes */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Quote Reminders</h3>
            <div className="space-y-1 rounded-lg border divide-y">
              {/* After Send */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={settings.quote_reminder_after_send_enabled}
                      onCheckedChange={(v) => update('quote_reminder_after_send_enabled', !!v)}
                    />
                    <span className="text-sm font-medium">Follow-up after sending</span>
                  </label>
                  <div className="flex items-center gap-2 text-sm">
                    <Input
                      type="number"
                      min={1}
                      max={30}
                      value={settings.quote_reminder_after_send_days}
                      onChange={(e) => update('quote_reminder_after_send_days', Number(e.target.value) || 1)}
                      disabled={!settings.quote_reminder_after_send_enabled}
                      className="w-16 h-9 text-center"
                    />
                    <span className="text-muted-foreground">days after</span>
                  </div>
                </div>
              </div>

              {/* Before Expiry */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={settings.quote_reminder_before_expiry_enabled}
                      onCheckedChange={(v) => update('quote_reminder_before_expiry_enabled', !!v)}
                    />
                    <span className="text-sm font-medium">Before expiration</span>
                  </label>
                  <div className="flex items-center gap-2 text-sm">
                    <Input
                      type="number"
                      min={1}
                      max={30}
                      value={settings.quote_reminder_before_expiry_days}
                      onChange={(e) => update('quote_reminder_before_expiry_days', Number(e.target.value) || 1)}
                      disabled={!settings.quote_reminder_before_expiry_enabled}
                      className="w-16 h-9 text-center"
                    />
                    <span className="text-muted-foreground">days before</span>
                  </div>
                </div>
              </div>

              {/* On Expiry */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={settings.quote_reminder_on_expiry_enabled}
                      onCheckedChange={(v) => update('quote_reminder_on_expiry_enabled', !!v)}
                    />
                    <span className="text-sm font-medium">On expiration day</span>
                  </label>
                  <span className="text-sm text-muted-foreground">Morning of expiration</span>
                </div>
              </div>
            </div>
          </div>

          {/* Note */}
          <p className="text-xs text-muted-foreground">
            Reminders stop automatically when invoices are paid or quotes are accepted.
          </p>
        </CardContent>
        )}
      </Card>

    </div>
  );
}
